import React, { useState } from "react";
import Loader from "../../../components/Loader";
import { formatToLocalDateTime } from "../utils/formatDate";
import { CustomModal } from "./CustomModal";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import URL_MAPPING from "../../../routes/constants";
import { deleteTrainingVideo, getTrainingVideoById } from "../api/trainingVideos";
import {
  TRAINING_VIDEO_DAY_FIELDS,
  TRAINING_VIDEO_WEEK_FIELDS,
} from "../utils/trainingVideoSchedule";
import eye from "../../../assets/eye.svg";

interface TrainingVideosTableProps {
  tableData: any;
  loading: boolean;
  refetch: boolean;
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
}

const WEEK_COLUMNS = TRAINING_VIDEO_WEEK_FIELDS;
const DAY_FIELDS = TRAINING_VIDEO_DAY_FIELDS;

const VIDEO_LANGUAGE_FIELDS = [
  { key: "video_link", label: "English" },
  { key: "video_link_ar", label: "Arabic" },
  { key: "video_link_ur", label: "Urdu" },
  { key: "video_link_hi", label: "Hindi" },
  { key: "video_link_bangla", label: "Bangla" },
] as const;

const COLUMN_COUNT = 10 + WEEK_COLUMNS.length;

const getAvailableVideoLinks = (data: any) =>
  VIDEO_LANGUAGE_FIELDS.filter(({ key }) => data?.[key]).map(({ key, label }) => ({
    label,
    url: data[key],
  }));

const getListDisplay = (values: unknown) => {
  if (!values) return "-";

  if (Array.isArray(values)) {
    return values.length > 0 ? values.join(", ") : "-";
  }

  if (typeof values === "string") {
    try {
      const parsed = JSON.parse(values);
      if (Array.isArray(parsed)) {
        return parsed.length > 0 ? parsed.join(", ") : "-";
      }
    } catch {
      return values.trim() ? values : "-";
    }
  }

  return "-";
};

const getVideoLanguagesDisplay = (item: any) => {
  const languages = VIDEO_LANGUAGE_FIELDS.filter(({ key }) => item?.[key]).map(
    ({ label }) => label
  );

  return languages.length > 0 ? languages.join(", ") : "-";
};

const getWeekDaysDisplay = (item: any, weekKey: string) => {
  if (!item?.[weekKey]) return "-";

  const activeDays = DAY_FIELDS.filter(({ key }) => item[key]).map(
    ({ label }) => label
  );

  return activeDays.length > 0 ? activeDays.join(", ") : "-";
};

const TrainingVideosTable: React.FC<TrainingVideosTableProps> = ({
  tableData,
  loading,
  refetch,
  setRefetch,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | number | null>(
    null
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [previewVideos, setPreviewVideos] = useState<
    { label: string; url: string }[]
  >([]);
  const [selectedPreviewIndex, setSelectedPreviewIndex] = useState(0);
  const navigate = useNavigate();

  const items = Array.isArray(tableData?.items)
    ? tableData.items
    : Array.isArray(tableData)
      ? tableData
      : [];

  const handleModalActivity = () => {
    setIsOpen(!isOpen);
  };

  const handleEdit = (id: string | number) => {
    navigate(URL_MAPPING.EDITTRAININGVIDEO(String(id)));
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewTitle("");
    setPreviewVideos([]);
    setSelectedPreviewIndex(0);
  };

  const handlePreview = async (id: string | number) => {
    try {
      setPreviewLoading(true);
      setPreviewOpen(true);
      setPreviewTitle("");
      setPreviewVideos([]);
      setSelectedPreviewIndex(0);

      const data = await getTrainingVideoById(id);
      const videos = getAvailableVideoLinks(data);

      if (videos.length === 0) {
        toast.error("No video link available for preview.");
        closePreview();
        return;
      }

      setPreviewTitle(data?.title || "Training Video");
      setPreviewVideos(videos);
    } catch (error: any) {
      console.error("Failed to fetch training video preview:", error);
      closePreview();
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail.forEach((err) => toast.error(err.msg));
      } else {
        toast.error(detail?.msg || detail || "Failed to load video preview.");
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedItemId === null) {
        toast.error("No training video selected for deletion");
        return;
      }

      const response = await deleteTrainingVideo(selectedItemId);
      if (!response) {
        toast.error("No data received or malformed response");
        return;
      }

      toast.success(response?.message || "Training video deleted successfully.");
      handleModalActivity();
      setRefetch(!refetch);
    } catch (error: any) {
      console.error("Failed to delete training video:", error);
      const detail = error?.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail.forEach((err) => toast.error(err.msg));
      } else {
        toast.error(detail?.msg || detail || "Failed to delete training video.");
      }
    }
  };

  return (
    <>
      <ToastContainer />
      <CustomModal
        isOpen={isOpen}
        onClose={handleModalActivity}
        onDelete={handleDelete}
      />
      {previewOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
          <div className="relative bg-white rounded-lg shadow-lg w-full max-w-3xl p-4">
            <button
              type="button"
              className="absolute top-3 right-3 text-gray-500 hover:text-black text-xl leading-none"
              onClick={closePreview}
            >
              ×
            </button>
            <h3 className="text-lg font-semibold pr-8 mb-3">
              {previewTitle || "Video Preview"}
            </h3>
            {previewLoading ? (
              <div className="flex justify-center items-center py-16">
                <Loader />
              </div>
            ) : (
              <>
                {previewVideos.length > 1 && (
                  <div className="mb-3">
                    <label className="block text-sm font-semibold mb-1">
                      Language
                    </label>
                    <select
                      value={selectedPreviewIndex}
                      onChange={(e) =>
                        setSelectedPreviewIndex(Number(e.target.value))
                      }
                      className="w-full border px-3 py-2 rounded"
                    >
                      {previewVideos.map(({ label }, index) => (
                        <option key={label} value={index}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <video
                  key={previewVideos[selectedPreviewIndex]?.url}
                  src={previewVideos[selectedPreviewIndex]?.url}
                  controls
                  className="w-full max-h-[60vh] rounded bg-black"
                >
                  Your browser does not support the video tag.
                </video>
              </>
            )}
          </div>
        </div>
      )}
      <div className="h-[calc(100vh-200px)] md:h-[calc(100vh-200px)] overflow-auto page-container p-2 pt-0 scrollbar scrollbar-thin">
        <table className="min-w-full bg-white border border-black-200 table-auto border-separate border-spacing-0">
          <thead className="bg-gray-200 sticky top-[0px] z-20">
            <tr>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                ID
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                Title
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                Start Date
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                End Date
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap min-w-[120px]">
                Sales Office
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap min-w-[100px]">
                Channel
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap min-w-[120px]">
                Video Language
              </th>
             
              {WEEK_COLUMNS.map(({ key, label }) => (
                <th
                  key={key}
                  className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap min-w-[100px]"
                >
                  {label}
                </th>
              ))}
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                Created At
              </th>
              <th className="border border-gray-300 text-13 font-bold text-black-500 whitespace-nowrap px-2 py-2 w-1">
              Preview
              </th>
              <th className="px-4 py-3 border border-gray-300 text-left text-13 font-bold text-black-500 whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          {loading ? (
            <tbody>
              <tr>
                <td colSpan={COLUMN_COUNT} className="py-8">
                  <div className="flex justify-center items-center w-full">
                    <Loader />
                  </div>
                </td>
              </tr>
            </tbody>
          ) : items.length === 0 ? (
            <tbody>
              <tr>
                <td colSpan={COLUMN_COUNT} className="py-8 text-center text-gray-500">
                  No results found
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {items.map((item: any, index: number) => (
                <tr key={item.id ?? index} className="border-b text-sm">
                  <td className="border border-gray-300 px-4 py-2">
                    {item.id ?? "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {item.title || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {item.video_start_date || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {item.video_end_date || "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-xs whitespace-normal min-w-[120px]">
                    {getListDisplay(item.sales_offices)}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-xs whitespace-normal min-w-[100px]">
                    {getListDisplay(item.channels)}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-xs whitespace-normal min-w-[120px]">
                    {getVideoLanguagesDisplay(item)}
                  </td>
                 
                  {WEEK_COLUMNS.map(({ key }) => (
                    <td
                      key={key}
                      className="border border-gray-300 px-4 py-2 text-xs whitespace-normal min-w-[100px]"
                    >
                      {getWeekDaysDisplay(item, key)}
                    </td>
                  ))}
                  <td className="border border-gray-300 px-4 py-2">
                    {item.created_at
                      ? formatToLocalDateTime(item.created_at)
                      : "-"}
                  </td>
                  <td className="border border-gray-300 px-4 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => handlePreview(item.id)}
                      disabled={previewLoading}
                      className="flex justify-center items-center w-full disabled:opacity-50"
                      aria-label="Preview video"
                    >
                      <img className="w-4 h-4" src={eye} alt="Preview video" />
                    </button>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(item.id)}
                        className="bg-gray-200 text-gray-700 px-3 py-1 rounded hover:bg-gray-300 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedItemId(item.id);
                          handleModalActivity();
                        }}
                        className="bg-red-100 text-red-700 px-3 py-1 rounded hover:bg-red-200 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
    </>
  );
};

export default TrainingVideosTable;
